import { Box, Card, Grid, Typography } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import componentStylesGeneric from "assets/theme/views/admin/generic.js";
import clsx from "clsx";
import GenericButton from "components/Buttons/GenericButton";
import FormDropdown from "components/FormFields/FormDropdown";
import LocationHeader from "components/Headers/LocationHeader";
import PageTitleHeader from "components/Headers/PageTitleHeader";
import { deleteContractApproval, getContractApprovalDetails, submitContractApproval } from "crud/contractApproval.crud";
import { listContractApprovalWorkCenterEmployee } from "crud/contractApprovalWorkCenterEmployee.crud";
import { ConditionGuard } from "guards/ConditionGuard";
import { useDataset } from "hooks/useDataset";
import useQueryParams from "hooks/useQueryParams";
import React, { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { connect, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import getDataSetByNameAction from "redux/actions/datasetsAction";
import { showGenericTwoButtonDialogAction } from "redux/actions/dialogsAction";
import { DIALOG } from "utils/actionsConsts";
import { CONTRACT_APPROVAL_STATUS, CONTRACT_TYPES_KEYVALUES, DATASET } from "utils/const";
import { getKeyValueByCode } from "utils/datasets";
import ContractApprovalDetailsTable from "../components/ContractApprovalDetailsTable";
import { ContractApprovalRequestDetailsCard } from "../components/ContractApprovalRequestDetailsCard";
import { ContractApprovalRequestObservations } from "../components/ContractApprovalRequestObservations";
import ContractApprovalWorkerDetailsDialog from "../components/ContractApprovalWorkerDetailsDialog";
import ContractApprovalWorkerDialog from "../components/ContractApprovalWorkerDialog";
import PaymentModal from "../components/PaymentModal";

const useStylesGeneric = makeStyles(componentStylesGeneric);

const useStylesLocal = makeStyles((theme) => ({
  cardMobilePadding: {
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(2),
    },
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      rowGap: theme.spacing(1),
    },
  },
  headerActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: "37px",
    [theme.breakpoints.down("sm")]: {
      marginBottom: 0,
      width: "100%",
    },
  },
  buttonMobile: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      marginTop: "0!important",
      marginRight: "0!important",
    },
  },
  footerActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: "20px",
    gap: "20px",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: "12px",
    },
  },
}));

const WORKERS_PAGE_INDEX_KEY = "cad_pageIndex";

const ContractApprovalDetails = (props) => {
  const classes = useStylesGeneric();
  const localClasses = useStylesLocal();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const [openDialog, setOpenDialog] = React.useState(false);
  const [openCreateDialog, setOpenCreateDialog] = React.useState(false);
  const [showSearchModal, setShowSearchModal] = React.useState(false);

  const [isOpenPaymentModal, setIsOpenPaymentModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const dispatch = useDispatch();
  const { id } = useParams();
  const location = useLocation();
  const { getQueryParams, setQueryParams } = useQueryParams();
  const redirect = true;
  const queryClient = useQueryClient();

  const listBackLink = location?.state?.from || "/admin/contract-approval/list";

  const { data: PROVINCES } = useDataset({ name: DATASET.PROVINCES });

  const initialPageIndexFromUrl = getQueryParams()?.[WORKERS_PAGE_INDEX_KEY] || 1;
  const { control, watch, getValues, handleSubmit, trigger, setValue } = useForm({
    defaultValues: {
      provinceId: 4,
      employmentCenterId: 33,
      pageIndex: initialPageIndexFromUrl,
    },
  });

  const pageIndex = watch("pageIndex") ?? 1;

  const provinceId = watch("provinceId");
  const provinceKeyValue = useMemo(() => getKeyValueByCode(PROVINCES, provinceId), [PROVINCES, provinceId]);

  const { data: EMPLOYMENT_CENTERS } = useDataset(
    { name: DATASET.EMPLOYMENT_CENTERS, parentKeyValue: provinceKeyValue },
    {
      enabled: !!provinceKeyValue,
    }
  );

  const handleChangePage = (_, value) => {
    setValue("pageIndex", value);

    const params = getQueryParams();
    if (value > 1) {
      params[WORKERS_PAGE_INDEX_KEY] = value;
    } else {
      delete params[WORKERS_PAGE_INDEX_KEY];
    }
    setQueryParams(params, true);
  };

  React.useEffect(() => {
    const next = getQueryParams()?.[WORKERS_PAGE_INDEX_KEY] || 1;
    const current = getValues("pageIndex") || 1;
    if (next !== current) {
      setValue("pageIndex", next);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps


  const handleOpenCreateDialog = () => {
    setOpenCreateDialog(true);
    setShowSearchModal(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
    queryClient.invalidateQueries({ queryKey: ["listEmployee", id] });
  };

  const handleOpenDialog = (id) => {
    setSelectedId(id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedId(null);
  };

  const handleClosePaymentModal = () => {
    setIsOpenPaymentModal(false);
  };

  const handlePaymentInformation = () => {
    setIsOpenPaymentModal(true);
  };

  const goBack = () => {
    // Prefer voltar para a lista com os filtros (quando veio da listagem)
    if (location?.state?.from) {
      props.history.push(location.state.from);
      return;
    }

    // Fallback: tenta voltar no histórico, senão vai para a lista padrão
    if (props.history?.length > 1) {
      props.history.goBack();
      return;
    }

    props.history.push("/admin/contract-approval/list");
  };

  const fetchContractApprovalDetails = useCallback(() => {
    return getContractApprovalDetails(id ?? "");
  }, [id]);

  const { data: contractApprovalDetails } = useQuery({
    queryKey: ["contractApprovalDetails", id],
    queryFn: fetchContractApprovalDetails,
  });

  const { mutateAsync: submitRequest } = useMutation(
    ["submitContractApprovalRequest"],
    async () =>
      submitContractApproval({
        id: id,
        provinceId: getValues().provinceId,
        employmentCenterId: getValues().employmentCenterId,
      }),
    {
      onSuccess: () => {
        dispatch({
          type: DIALOG.SHOW,
          payload: {
            showGenericDialog: true,
            genericDialogData: {
              type: DIALOG.ONE_BUTTON_TYPE,
              isSuccess: true,
              goBack: true,
              buttonLabel: "Ok",
              onClose: listBackLink,
              message: "Pedido de homologação submetido com sucesso.",
            },
          },
        });
      },
      onError: (error) => {
        let errorMessage = "Ocorreu um erro ao eliminar o trabalhador.";
        if (error.response && error.response.data && error.response.data.errors) {
          const errors = error.response.data.errors;
          errorMessage = Object.values(errors).flat().join("\n");
        }
        dispatch({
          type: DIALOG.SHOW,
          payload: {
            showGenericDialog: true,
            genericDialogData: {
              type: DIALOG.ONE_BUTTON_TYPE,
              isSuccess: false,
              message: errorMessage,
            },
          },
        });
      },
    }
  );

  const { mutateAsync: handleDeleteAsync } = useMutation(
    ["createWorkCenterEmployer"],
    async (id) => deleteContractApproval(id),
    {
      onSuccess: () => {
        dispatch({
          type: DIALOG.SHOW,
          payload: {
            showGenericDialog: true,
            genericDialogData: {
              type: DIALOG.ONE_BUTTON_TYPE,
              isSuccess: true,
              goBack: redirect,
              buttonLabel: "Ok",
              onClose: redirect ? listBackLink : null,
              message: "Pedido eliminado com sucesso.",
            },
          },
        });
      },
      onError: (error) => {
        console.log(error);

        dispatch({
          type: DIALOG.SHOW,
          payload: {
            showGenericDialog: true,
            genericDialogData: {
              type: DIALOG.ONE_BUTTON_TYPE,
              isSuccess: false,
              message: "Ocorreu um erro a efetuar o seu pedido, por favor tente mais tarde.",
            },
          },
        });
      },
    }
  );

  const { mutateAsync: handleCancelAsync } = useMutation(
    ["cancelContractApprovalRequest", id],
    async () => deleteContractApproval(id),
    {
      onSuccess: () => {
        dispatch({
          type: DIALOG.SHOW,
          payload: {
            showGenericDialog: true,
            genericDialogData: {
              type: DIALOG.ONE_BUTTON_TYPE,
              isSuccess: true,
              goBack: redirect,
              buttonLabel: "Ok",
              onClose: redirect ? listBackLink : null,
              message: "Pedido de homologação de contratos cancelado com sucesso.",
            },
          },
        });
        queryClient.invalidateQueries(["contractApprovalDetails", id]);
        queryClient.invalidateQueries(["listContractApproval"]);
      },
      onError: (error) => {
        console.log(error);

        dispatch({
          type: DIALOG.SHOW,
          payload: {
            showGenericDialog: true,
            genericDialogData: {
              type: DIALOG.ONE_BUTTON_TYPE,
              isSuccess: false,
              message: "Ocorreu um erro ao cancelar o pedido, por favor tente mais tarde.",
            },
          },
        });
      },
    }
  );

  const deleteRequest = async () => {
    if (!id) {
      throw new Error("O id do pedido não está definido.");
    }
    await handleDeleteAsync(id);
  };

  const cancelRequest = async () => {
    if (!id) {
      throw new Error("O id do pedido não está definido.");
    }
    await handleCancelAsync();
  };

  const fetchContractApprovalWorkersList = useCallback(() => {
    return listContractApprovalWorkCenterEmployee(id ?? "", pageIndex);
  }, [id, pageIndex]);

  const { data: listEmployee, isLoading } = useQuery({
    queryKey: ["listEmployee", id, pageIndex],
    queryFn: fetchContractApprovalWorkersList,
  });

  const showAddPaymentDataButton = listEmployee?.data?.data?.some(
    (worker) =>
      worker?.contractTypeKeyValue === CONTRACT_TYPES_KEYVALUES.TEMPO_DETERMINADO_ESTRANGEIROS_NAO_RESIDENTES
  );

  const isDraft = contractApprovalDetails?.contractApprovalStatusKeyValue === CONTRACT_APPROVAL_STATUS.CREATED;

  const awaitingValidation =
    contractApprovalDetails?.contractApprovalStatusKeyValue === CONTRACT_APPROVAL_STATUS.AWAITING_VALIDATION;

  const awaitingPaymentData =
    contractApprovalDetails?.contractApprovalStatusKeyValue === CONTRACT_APPROVAL_STATUS.AWAITING_PAYMENT_DATA;

  const canCancelAwaitingPayment =
    contractApprovalDetails?.contractApprovalStatusKeyValue === CONTRACT_APPROVAL_STATUS.AWAITING_PAYMENT ||
    contractApprovalDetails?.contractApprovalStatusKeyValue === CONTRACT_APPROVAL_STATUS.AWAITING_PAYMENT_DATA;

  const showDialog = (message, submessage, yesAction, noAction) => {
    dispatch(showGenericTwoButtonDialogAction(message, submessage, yesAction, noAction));
  };

  const SubmitRequestConfirmation = () => {
    trigger().then((res) => {
      if (!res) {
        return;
      } else {
        showDialog(
          "Submeter Pedido",
          "Tem a certeza que deseja submeter o pedido de homologação de contrato?",
          () => submitRequest(),
          () => {}
        );
      }
    });
  };

  const cancelRequestConfirmation = () => {
    showDialog(
      "Cancelar Pedido de Homologação",
      "Tem a certeza que deseja cancelar o pedido de homologação de contratos?",
      async () => {
        await cancelRequest();
      },
      () => {}
    );
  };

  return (
    <Grid container classes={{ root: classes.containerRoot }}>
      <Grid item xs={12} md={12}>
        <LocationHeader
          iconButton={false}
          section="Detalhes"
          subsection="Lista de Pedidos de Homologação de Contrato"
          subsectionLink={listBackLink}
        />{" "}
      </Grid>

      <Box className={localClasses.headerRow}>
        <Box>
          <PageTitleHeader title={"Detalhes Pedido de Homologação"} />
        </Box>
        <ConditionGuard condition={!isDraft && showAddPaymentDataButton}>
          <Box className={localClasses.headerActions}>
            <GenericButton
              typeSubmit={false}
              color="tertiary"
              size="large"
              onClick={() => handlePaymentInformation()}
              classes={classes.TableCardHeaderButton}
              className={localClasses.buttonMobile}
              fullWidth={isSmDown}
              disabled={awaitingPaymentData}
            >
              Informações de Pagamento
            </GenericButton>
          </Box>
        </ConditionGuard>
      </Box>

      <ContractApprovalRequestDetailsCard contractApprovalRequest={contractApprovalDetails} isDraft={isDraft} />

      <ConditionGuard condition={isDraft}>
        <Grid item xs={12} md={12}>
          <Card className={clsx(classes.cardRoot, localClasses.cardMobilePadding)}>
            <form onSubmit={handleSubmit(submitRequest)}>
              <Grid container>
                <Grid item xs={12}>
                  <Typography variant="h2" style={{ color: "#002e9e", marginBottom: "20px" }}>
                    Centro de Emprego
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormDropdown
                    control={control}
                    label="Província Centro de Emprego"
                    name="provinceId"
                    options={PROVINCES}
                    mappingTo="code"
                    required
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormDropdown
                    control={control}
                    label="Centro de Emprego"
                    name="employmentCenterId"
                    options={EMPLOYMENT_CENTERS}
                    required
                    mappingTo="code"
                    disabled
                  />
                </Grid>
              </Grid>
            </form>
          </Card>
        </Grid>
      </ConditionGuard>
      <ConditionGuard condition={isDraft}>
        <Grid item xs={12} md={12}>
          <GenericButton
            typeSubmit={false}
            color="tertiary"
            size="large"
            onClick={() => handleOpenCreateDialog()}
            classes={classes.TableCardHeaderButton}
            className={localClasses.buttonMobile}
            fullWidth={isSmDown}
          >
            Adicionar Trabalhador
          </GenericButton>
        </Grid>{" "}
      </ConditionGuard>
      <Grid item xs={12} md={12}>
        <Box
          classes={{ root: classes.tableResultBoxRoot }}
          sx={{
            mt: 3,
          }}
        >
          <ContractApprovalDetailsTable
            page={pageIndex}
            handleChangePage={handleChangePage}
            handleOpenDialog={handleOpenDialog}
            listEmployee={listEmployee}
            isLoading={isLoading}
          />
        </Box>
      </Grid>

      <ConditionGuard condition={!isDraft}>
        <ContractApprovalRequestObservations observations={contractApprovalDetails} />
      </ConditionGuard>
      <Grid item xs={12} md={12} className={localClasses.footerActions}>
        <GenericButton size="medium" color="tertiary" onClick={() => goBack()} fullWidth={isSmDown}>
          {"Voltar á Página Inicial"}
        </GenericButton>
        <ConditionGuard condition={canCancelAwaitingPayment}>
          <GenericButton
            size="medium"
            color="tertiaryRed"
            onClick={() => cancelRequestConfirmation()}
            fullWidth={isSmDown}
          >
            {"Cancelar Pedido Homologação"}
          </GenericButton>
        </ConditionGuard>
        <ConditionGuard condition={isDraft}>
          <GenericButton size="medium" color="tertiaryRed" onClick={() => deleteRequest()} fullWidth={isSmDown}>
            {"Eliminar Pedido"}
          </GenericButton>
          <GenericButton size="medium" color="primary" onClick={() => SubmitRequestConfirmation()} fullWidth={isSmDown}>
            {"Submeter Pedido"}
          </GenericButton>
        </ConditionGuard>
      </Grid>
      {openDialog && (
        <ContractApprovalWorkerDetailsDialog
          openDialog={openDialog}
          handleCloseDetailsDialog={handleCloseDialog}
          getDataSetByName={props.getDataSetByName}
          props={props}
          workerId={selectedId}
          isDraft={isDraft}
          requestNumber={id}
          stateKeyValue={contractApprovalDetails?.contractApprovalStatusKeyValue}
        />
      )}

      {openCreateDialog && (
        <ContractApprovalWorkerDialog
          openDialog={openCreateDialog}
          handleCloseDialog={handleCloseCreateDialog}
          getDataSetByName={props.getDataSetByName}
          props={props}
          showSearchModal={showSearchModal}
          contractApprovalId={contractApprovalDetails?.id}
        />
      )}

      {isOpenPaymentModal && (
        <PaymentModal
          isOpenPaymentModal={isOpenPaymentModal}
          handleClosePaymentModal={handleClosePaymentModal}
          awaitingValidation={awaitingValidation}
        />
      )}
    </Grid>
  );
};

const mapStateToProps = (state) => ({ ...state });

const mapDispatchToProps = (dispatch) => ({
  getDataSetByName: (name, addEmptyField) => dispatch(getDataSetByNameAction(name, addEmptyField)),
});
export default connect(mapStateToProps, mapDispatchToProps)(ContractApprovalDetails);
